import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, Sparkles, X } from 'lucide-react';
import { DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import Title from '../ui/Title';
import { Button } from '../ui/button';
import Loader from '../ui/Loader';
import {
  formatPrice,
  getMonthlyEquivalentAmount,
  getPriceByInterval,
  getPricingInfo,
  getPurchasablePlans,
  PLAN_ADVANTAGE_COUNT,
} from './usageExpiredUtils';
import { getPaymentSummary } from '@/lib/subscriptionPricing';
import type { StripePriceDto } from '@/features/billing/types';
import { useAppDispatch } from '@/app/hooks';
import { logout } from '@/features/auth/authThunks';
import { cn } from '@/lib/utils';

type PlanSectionProps = {
  prices: StripePriceDto[] | null;
  isTrial: boolean;
  isExpired: boolean;
  onSelectPlan: (planKey: string) => void;
  currentPlanName?: string | null;
  isWelyaUser?: boolean;
  isPartnerOffer?: boolean;
  isYearly: boolean;
  onIsYearlyChange: (value: boolean) => void;
};

function PlanIntervalTabs({
  prices,
  planKey,
  isYearly,
  onIsYearlyChange,
}: Readonly<{
  prices: StripePriceDto[] | null;
  planKey: string;
  isYearly: boolean;
  onIsYearlyChange: (value: boolean) => void;
}>) {
  const { t } = useTranslation('billing');
  const pricingInfo = getPricingInfo(prices, planKey);
  const monthlyPrice = getPriceByInterval(prices, planKey, 'month');
  const yearlyPrice = getPriceByInterval(prices, planKey, 'year');
  if (!pricingInfo || !monthlyPrice) {
    return <p className="text-sm text-secondary">{t('planSection.loadingPrices')}</p>;
  }

  const { savings } = pricingInfo;
  const currency = monthlyPrice.currency;
  // La grille annonce un tarif « par mois » : on affiche donc le montant reconduit, pas celui d'une promo ponctuelle.
  const monthlySummary = getPaymentSummary(monthlyPrice);
  const yearlySummary = yearlyPrice ? getPaymentSummary(yearlyPrice) : null;
  const hasMonthlyDiscount = monthlySummary.hasRecurringDiscount;
  const hasYearlyDiscount = Boolean(yearlySummary?.hasRecurringDiscount);
  const yearlyBaseAmount = yearlySummary?.renewalCents ?? monthlyPrice.amount * 12;
  const showMonthlyTrial = monthlySummary.showTrialNotice;
  const showYearlyTrial = yearlySummary?.showTrialNotice ?? false;

  return (
    <Tabs
      value={isYearly ? 'yearly' : 'monthly'}
      onValueChange={(v: string) => onIsYearlyChange(v === 'yearly')}
    >
      <TabsList className="w-full">
        <TabsTrigger value="yearly">{t('planSection.yearly')}</TabsTrigger>
        <TabsTrigger value="monthly">{t('planSection.monthly')}</TabsTrigger>
      </TabsList>
      <TabsContent value="yearly" className="flex flex-col gap-1 pt-6">
        <p className="flex items-baseline gap-2 flex-wrap">
          {hasYearlyDiscount && yearlyPrice ? (
            <span className="text-base line-through text-tertiary">
              {formatPrice(getMonthlyEquivalentAmount(yearlyPrice.amount), currency)}
              {t('planSection.perMonth')}
            </span>
          ) : null}
          <span className="text-2xl text-highlighted font-semibold">
            {formatPrice(getMonthlyEquivalentAmount(yearlyBaseAmount), currency)}
            {t('planSection.perMonth')}
          </span>
          <span className="text-sm text-secondary">{t('planSection.billedYearly')}</span>
        </p>
        {yearlyPrice && savings > 0 ? (
          <p className="text-sm">
            {t('planSection.savingsPrefix')}{' '}
            <span className="font-semibold">{formatPrice(savings, currency)}</span>{' '}
            {t('planSection.savingsSuffix')}
          </p>
        ) : null}
        {showYearlyTrial ? (
          <p className="text-sm font-semibold text-highlighted">{t('trialOffer.badge')}</p>
        ) : null}
      </TabsContent>
      <TabsContent value="monthly" className="flex flex-col gap-1 pt-6">
        {hasMonthlyDiscount ? (
          <p className="flex items-baseline gap-2">
            <span className="text-base line-through text-tertiary">
              {formatPrice(monthlyPrice.amount, currency)}
            </span>
            <span className="text-2xl text-highlighted font-semibold">
              {formatPrice(monthlySummary.renewalCents, currency)}
              {t('planSection.perMonth')}
            </span>
          </p>
        ) : (
          <p className="text-2xl text-highlighted font-semibold">
            {formatPrice(monthlyPrice.amount, currency)}
            {t('planSection.perMonth')}
          </p>
        )}
        {showMonthlyTrial ? (
          <p className="text-sm font-semibold text-highlighted">{t('trialOffer.badge')}</p>
        ) : null}
      </TabsContent>
    </Tabs>
  );
}

export default function PlanSection({
  prices,
  isTrial,
  isExpired,
  onSelectPlan,
  currentPlanName,
  isWelyaUser = false,
  isPartnerOffer = false,
  isYearly,
  onIsYearlyChange,
}: PlanSectionProps) {
  const { t } = useTranslation('billing');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const visiblePlans = getPurchasablePlans(prices);

  const handleLogout = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    try {
      const result = await dispatch(logout());
      if (logout.fulfilled.match(result)) {
        navigate('/login', { replace: true });
      }
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <div className="space-y-8">
      {isExpired && (
        <DialogHeader className="flex flex-col items-center gap-2">
          <DialogTitle className="text-center">
            {t(
              isPartnerOffer
                ? 'planSection.partnerOfferEndedTitle'
                : 'planSection.trialExpiredTitle',
            )}
          </DialogTitle>
          <DialogDescription className="text-secondary text-sm text-center">
            {t(
              isPartnerOffer
                ? 'planSection.partnerOfferEndedDescription'
                : 'planSection.trialExpiredDescription',
            )}
          </DialogDescription>
        </DialogHeader>
      )}
      {isTrial && !isExpired && (
        <DialogHeader className="flex flex-col items-center gap-2">
          <DialogTitle className="text-center">{t('planSection.unlockTitle')}</DialogTitle>
          <DialogDescription className="text-secondary text-sm text-center">
            {t('planSection.unlockDescription')}
          </DialogDescription>
        </DialogHeader>
      )}
      <div
        className={cn(
          // Cartes alignées par le bas : le forfait mis en avant est plus haut et dépasse donc par le haut.
          'grid grid-cols-1 items-end gap-6',
          visiblePlans.length === 1 && 'max-w-md mx-auto',
          visiblePlans.length === 2 && 'sm:grid-cols-2 max-w-3xl mx-auto',
          visiblePlans.length > 2 && 'sm:grid-cols-2 lg:grid-cols-3',
        )}
      >
        {visiblePlans.map((plan, index) => {
          const isCurrentPlan =
            currentPlanName &&
            (plan.name.toLowerCase() === currentPlanName.toLowerCase() ||
              (plan.priceKey && plan.priceKey.toLowerCase() === currentPlanName.toLowerCase()));
          const isLastOnOrphanRow =
            visiblePlans.length > 2 &&
            index === visiblePlans.length - 1 &&
            visiblePlans.length % 2 !== 0;
          return (
            <div
              key={plan.name}
              className={cn(
                'flex flex-col justify-between p-4 rounded-2xl border border-[#C5C5CA]',
                plan.highlighted && 'border-2 border-highlighted',
                isLastOnOrphanRow &&
                  'sm:col-span-2 sm:max-w-[calc(50%-0.75rem)] sm:justify-self-center lg:col-span-1 lg:max-w-none lg:justify-self-auto',
              )}
            >
              <div className="relative mb-6">
                <img
                  src={plan.img}
                  alt=""
                  width={1200}
                  height={750}
                  loading="lazy"
                  decoding="async"
                  className={cn(
                    'w-full rounded-lg object-cover',
                    plan.highlighted ? 'h-[200px]' : 'h-[180px]',
                  )}
                />
                {/* Pastille volontairement de travers dans la maquette : 3° vers la gauche. */}
                {plan.highlighted && (
                  <span className="absolute -bottom-4 right-4 flex -rotate-3 items-center gap-1 rounded-full bg-button-secondary py-1 pl-3 pr-2 text-base font-medium text-dark shadow-[0_0_13px_rgba(131,131,131,0.6)]">
                    {t('planSection.mostPopular')}
                    <Sparkles size={16} />
                  </span>
                )}
              </div>

              <div className="space-y-2">
                <Title level={2}>
                  {t(`planSection.plans.${plan.priceKey.toLowerCase()}.title`)}
                </Title>
              </div>

              <div className="pb-6">
                <PlanIntervalTabs
                  prices={prices}
                  planKey={plan.priceKey ?? plan.name}
                  isYearly={isYearly}
                  onIsYearlyChange={onIsYearlyChange}
                />
              </div>

              <div className="flex flex-col gap-2 pb-6">
                {Array.from({ length: PLAN_ADVANTAGE_COUNT }, (_, index) => {
                  const isIncluded = !plan.excludedAdvantages.includes(index);
                  return (
                    <div key={index} className="flex items-center gap-2">
                      {isIncluded ? (
                        <Check size={16} className="text-success shrink-0" />
                      ) : (
                        <X size={16} className="text-tertiary shrink-0" />
                      )}
                      <p className={isIncluded ? 'text-secondary' : 'text-tertiary line-through'}>
                        {t(`planSection.plans.${plan.priceKey.toLowerCase()}.advantage_${index}`)}
                      </p>
                    </div>
                  );
                })}
              </div>

              <Button
                type="button"
                variant={plan.highlighted ? 'default' : 'outline'}
                className="w-full"
                disabled={!!isCurrentPlan}
                onClick={() => onSelectPlan(plan.priceKey)}
              >
                {t(
                  `planSection.plans.${plan.priceKey.toLowerCase()}.${
                    isYearly ? 'buttonLabelYearly' : 'buttonLabelMonthly'
                  }`,
                )}
              </Button>
            </div>
          );
        })}
      </div>
      {isWelyaUser && (
        <Button
          type="button"
          className="w-full bg-red-500 text-white"
          disabled={isLoggingOut}
          onClick={handleLogout}
        >
          {isLoggingOut && <Loader />}
          {t('planSection.logout')}
        </Button>
      )}
    </div>
  );
}
